import React, { useState, useCallback, useEffect } from "react";
import { Card, message, Spin, Result, Alert, Select, Space } from "antd";
import { useSelector } from "react-redux";
import SchemaTreeEditor, {
  PendingAddition,
  PendingUnionAddition,
  PendingTableFieldAddition,
} from "./SchemaTreeEditor";
import GitHubPatSetup from "./GitHubPatSetup";
import SchemaDiffPreview from "./SchemaDiffPreview";
import {
  fetchGitHubFileContent,
  fetchRepoBranches,
  createAndMergeSchemaPR,
  generateFileChanges,
  DEFAULT_SOURCE_BRANCH,
} from "./SchemaGitService";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
import { RootState } from "../../../app/store";
import {
  DeviceType,
  SchemaBranchNode,
  fetchSchemaTreeSkeleton,
  getCachedRawContent,
} from "./FlatBufferSchemaTree";
import { initWasm } from "./wasmParser";
import { validateAllChanges, initFlatc } from "./flatcValidator";

interface SchemaEditorPageProps {
  /** Pre-select device type based on bank config. Null = show both. */
  deviceType?: DeviceType | null;
}

function SchemaEditorPage({ deviceType = null }: SchemaEditorPageProps) {
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const [loading, setLoading] = useState(false);
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [githubToken, setGithubToken] = useState<string | null>(null);

  // Branch selection
  const [branches, setBranches] = useState<string[]>([]);
  const [sourceBranch, setSourceBranch] = useState(DEFAULT_SOURCE_BRANCH);
  const [targetBranch, setTargetBranch] = useState(DEFAULT_SOURCE_BRANCH);

  // Live schema tree state
  const [schemaTree, setSchemaTree] = useState<SchemaBranchNode | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // WASM initialization state
  const [wasmReady, setWasmReady] = useState(false);
  const [wasmError, setWasmError] = useState<string | null>(null);

  // Diff preview state
  const [showDiffPreview, setShowDiffPreview] = useState(false);
  const [pendingEnumAdditions, setPendingEnumAdditions] = useState<PendingAddition[]>([]);
  const [pendingUnionAdditions, setPendingUnionAdditions] = useState<PendingUnionAddition[]>([]);
  const [pendingTableAdditions, setPendingTableAdditions] = useState<PendingTableFieldAddition[]>(
    [],
  );
  const [previewFileContents, setPreviewFileContents] = useState<Record<string, string>>({});

  const isMasterAdmin =
    userLogged &&
    userLogged.role &&
    userLogged.role.name === process.env.REACT_APP_ROLE_MASTER_NAME;

  // Initialize WASM modules on mount (parser + flatc validator — both mandatory)
  useEffect(() => {
    let cancelled = false;
    Promise.all([initWasm(), initFlatc()])
      .then(() => {
        if (!cancelled) setWasmReady(true);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          let msg = "Failed to load WASM modules (parser + flatc)";
          if (err instanceof Error) msg = err.message;
          else if (typeof err === "string") msg = err;
          setWasmError(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch available branches when token becomes available
  useEffect(() => {
    if (!githubToken) return undefined;
    let cancelled = false;
    fetchRepoBranches(githubToken)
      .then((branchList) => {
        if (!cancelled) setBranches(branchList);
      })
      .catch(() => {
        // Non-fatal — branch picker will just show the default
      });
    return () => {
      cancelled = true;
    };
  }, [githubToken]);

  // Fetch schema tree skeleton when token + WASM + sourceBranch all available
  useEffect(() => {
    if (!githubToken || !wasmReady) {
      return undefined;
    }

    let cancelled = false;
    setSchemaLoading(true);
    setSchemaError(null);

    fetchSchemaTreeSkeleton(githubToken, sourceBranch)
      .then((tree) => {
        if (!cancelled) {
          setSchemaTree(tree);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSchemaError(err.message || "Failed to fetch schema");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSchemaLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [githubToken, wasmReady, sourceBranch]);

  /** Phase 1: Fetch files and show diff preview */
  const handleReviewChanges = useCallback(
    async (
      enumAdds: PendingAddition[],
      unionAdds: PendingUnionAddition[],
      tableAdds: PendingTableFieldAddition[],
    ) => {
      setLoading(true);
      setPrUrl(null);

      try {
        if (!githubToken) {
          message.error("GitHub token not available. Please configure your token first.");
          setLoading(false);
          return;
        }

        // Get unique file paths we need from all change types
        const filePaths = [
          ...new Set([
            ...enumAdds.map((a) => `schemas/${a.enumNode.fbsFile}`),
            ...unionAdds.map((a) => `schemas/${a.unionNode.fbsFile}`),
            ...tableAdds.map((a) => `schemas/${a.tableNode.fbsFile}`),
          ]),
        ];

        // Use cached content when available, fetch only what's missing
        const fileContents: Record<string, string> = {};
        const uncached = filePaths.filter((path) => {
          const cached = getCachedRawContent(path);
          if (cached) {
            fileContents[path] = cached;
            return false;
          }
          return true;
        });
        const fetchPromises = uncached.map(async (path) => {
          const { content } = await fetchGitHubFileContent(path, githubToken, sourceBranch);
          fileContents[path] = content;
        });
        await Promise.all(fetchPromises);

        // Store state and show diff preview
        setPendingEnumAdditions(enumAdds);
        setPendingUnionAdditions(unionAdds);
        setPendingTableAdditions(tableAdds);
        setPreviewFileContents(fileContents);
        setShowDiffPreview(true);
      } catch (err: any) {
        message.error(`Error: ${err.message || "Unknown error"}`);
      } finally {
        setLoading(false);
      }
    },
    [githubToken, sourceBranch],
  );

  /** Phase 2: Validate conformity, then create branch, commit, PR, and merge */
  const handleConfirmPR = useCallback(async () => {
    setShowDiffPreview(false);
    setLoading(true);

    try {
      if (!githubToken) {
        message.error("GitHub token not available. Please configure your token first.");
        setLoading(false);
        return;
      }

      // Generate file changes to validate
      const fileChanges = generateFileChanges(
        pendingEnumAdditions,
        pendingUnionAdditions,
        pendingTableAdditions,
        previewFileContents,
      );

      // Run flatc --conform validation before creating PR
      const conformChanges = fileChanges.map((change) => ({
        path: change.path,
        originalContent: previewFileContents[change.path] || "",
        modifiedContent: change.content,
      }));

      const { allValid, results } = await validateAllChanges(conformChanges);
      if (!allValid) {
        const failedFiles = Object.entries(results)
          .filter(([, r]) => !r.valid)
          .map(([path, r]) => `${path}: ${r.errors.join("; ")}`)
          .join("\n");
        message.error({
          content: `Schema conformity check failed. Changes are not backward-compatible:\n${failedFiles}`,
          duration: 10,
        });
        setLoading(false);
        return;
      }

      const { prUrl: url, merged } = await createAndMergeSchemaPR(
        githubToken,
        pendingEnumAdditions,
        pendingUnionAdditions,
        pendingTableAdditions,
        previewFileContents,
        targetBranch,
      );

      if (!merged) {
        message.warning(`PR created but auto-merge failed. Please merge manually: ${url}`);
      } else {
        message.success("Schema changes merged successfully!");
      }

      setPrUrl(url);
    } catch (err: any) {
      message.error(`Error: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, [
    githubToken,
    pendingEnumAdditions,
    pendingUnionAdditions,
    pendingTableAdditions,
    previewFileContents,
    targetBranch,
  ]);

  const handleCancelDiffPreview = useCallback(() => {
    setShowDiffPreview(false);
  }, []);

  if (!isMasterAdmin) {
    return <UnauthorizedPage />;
  }

  if (!hasPermission(Permission.OrgsView, permissionSet ?? [])) {
    return <UnauthorizedPage />;
  }

  // Show WASM loading error
  if (wasmError) {
    return (
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        <Alert type="error" message="Failed to load WASM parser" description={wasmError} showIcon />
      </Card>
    );
  }

  // Show the PAT setup when no token is available
  if (!githubToken) {
    return (
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        <GitHubPatSetup onTokenReady={setGithubToken} />
      </Card>
    );
  }

  // Show loading state while fetching live schema or WASM
  if (schemaLoading || !wasmReady) {
    return (
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        <Spin
          tip={!wasmReady ? "Loading WASM parser..." : "Discovering schema files from GitHub..."}
        >
          <div style={{ padding: 50, textAlign: "center" }} />
        </Spin>
      </Card>
    );
  }

  // Show error state if schema fetch failed
  if (schemaError) {
    return (
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        <Alert type="error" message="Failed to load schema" description={schemaError} showIcon />
      </Card>
    );
  }

  // Should not happen if loading finished without error, but guard against it
  if (!schemaTree) {
    return (
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        <Alert
          type="warning"
          message="Schema not available"
          description="The schema tree has not been loaded yet."
          showIcon
        />
      </Card>
    );
  }

  return (
    <Spin spinning={loading} tip="Creating PR and merging...">
      <Card title="FlatBuffer Schema Editor" style={{ margin: 24 }}>
        {prUrl ? (
          <Result
            status="success"
            title="Schema Changes Merged"
            subTitle={
              <span>
                Pull request:{" "}
                <a href={prUrl} target="_blank" rel="noopener noreferrer">
                  {prUrl}
                </a>
              </span>
            }
            extra={
              <p style={{ opacity: 0.65 }}>
                Remember to regenerate bindings (generate_fbs.bash, generate_go.bash,
                generate_ts.bash) after this change.
              </p>
            }
          />
        ) : (
          <>
            {branches.length > 0 && (
              <Space style={{ marginBottom: 16 }}>
                <span>Source branch:</span>
                <Select
                  value={sourceBranch}
                  onChange={setSourceBranch}
                  style={{ width: 280 }}
                  showSearch
                  options={branches.map((b) => ({ label: b, value: b }))}
                  size="small"
                />
                <span>Target branch (PR):</span>
                <Select
                  value={targetBranch}
                  onChange={setTargetBranch}
                  style={{ width: 280 }}
                  showSearch
                  options={branches.map((b) => ({ label: b, value: b }))}
                  size="small"
                />
              </Space>
            )}
            <SchemaTreeEditor
              deviceType={deviceType}
              schemaTree={schemaTree}
              githubToken={githubToken}
              onCreatePR={handleReviewChanges}
            />
          </>
        )}
      </Card>
      {showDiffPreview && (
        <SchemaDiffPreview
          additions={pendingEnumAdditions}
          fileContents={previewFileContents}
          onConfirm={handleConfirmPR}
          onCancel={handleCancelDiffPreview}
        />
      )}
    </Spin>
  );
}

export default SchemaEditorPage;
